import { useApolloClient } from '@apollo/client'
import { Box, Divider, Link as MUILink, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import invariant from 'invariant'
import { useEffect, useState } from 'react'

import {
  Button,
  Checkbox,
  Container,
  ContentBox,
  FormControl,
  FormControlLabel,
  Icons,
  Stack,
  Text,
  insufficientFundsExtraMessage,
  longWait,
  useAlert,
} from '../../components'
import { mutations, queries } from '../../graphql'
import { fromAmountField, join, toAmountString, toDateString, getTrackingData } from '../../utils'
import { calculateGiftAmount } from './gift-details-math'
import { PaymentMethodsMeta } from './payment'
import { GuestPaymentMethodsMeta } from './payment-guest/index'
import { onlyShowInDev } from './showUnfinishedFeatures'

export function ConfirmStep({
  donationTarget,
  donationTargetId,
  defaultFund,
  stepperState,
  dispatchStep,
  isDonatingToFund,
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    if (onlyShowInDev) console.log(`confirm stepperState: ${JSON.stringify(stepperState, null, 2)}`)
  }, [stepperState])

  const client = useApolloClient()
  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const { register, handleSubmit, formState, watch, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      anonymous: false,
    },
  })
  const { isSubmitting } = formState

  const getPurposeOfGiftId = (purposeOfGrant) => {
    if (donationTarget?.purposeOfGifts) {
      const purpose = donationTarget.purposeOfGifts.find((p) => p.reason === purposeOfGrant)
      if (purpose) return purpose.id
    }
    return null
  }

  const onSubmit = async ({ anonymous }) => {
    try {
      const {
        amountType,
        securities,
        isRecurring,
        recurringInterval,
        processingDate,
        purposeOfGrant,
        purposeOfGrantNote,
        selfAttestation,
      } = stepperState.giftDetails
      const { paymentMethodType, paymentMethodId, foundationDAFName } = stepperState.payment
      const amounts = calculateGiftAmount(stepperState.giftDetails)

      const fundId = stepperState.isGuest ? stepperState.fundId : stepperState.activeDonorPortal?.id ?? defaultFund?.id
      if (!fundId) throw new Error(`No fund available for contribution`)

      const donationType = isDonatingToFund ? 'GEN_FUND' : 'CHARITY'

      // Build deliveringCustodianInfo for securities payment type
      const selectedPath = stepperState.payment?.selectedPath
      const isAdvisorPath = selectedPath === 'advisor'
      let deliveringCustodianInfo
      if (paymentMethodType === 'securities') {
        if (isAdvisorPath) {
          deliveringCustodianInfo = {
            securitiesPath: 'advisor',
            custodianName: stepperState.payment.custodianName,
            advisorName: stepperState.payment.advisorName,
            advisorEmail: stepperState.payment.advisorEmail,
            intendedUse: 'liquidate',
          }
        } else {
          deliveringCustodianInfo = {
            securitiesPath: 'self-directed',
            accountNumber: stepperState.payment.accountNumber,
            address: stepperState.payment.address,
          }
        }
      }

      const baseVariables = {
        fundId,
        paymentMethodType,
        paymentMethodId,
        contributionTotalAmount: Number(amounts.total),
        amountBeforeTip: Number(amounts.amount),
        tip: Number(amounts.tip ?? 0),
        purposeOfGrant,
        purposeOfGiftId: getPurposeOfGiftId(purposeOfGrant),
        purposeOfGrantNote,
        isRecurring,
        recurringInterval,
        processingDate: processingDate ? new Date(processingDate) : null,
        selfAttestation,
        anonymous,
        securities,
        foundationDAFName,
        deliveringCustodianInfo,
        usedGiveGenius: true,
        donationType,
        ...(isDonatingToFund ? { genFundId: Number(donationTargetId) } : { charityId: donationTargetId }),
        ...getTrackingData(),
      }

      const mutation = stepperState.isGuest
        ? mutations.giveGenius.createGuestDonation
        : mutations.giveGenius.createDonation

      const activePortalHeader = stepperState.activeDonorPortal
        ? `kind=donor;id=${stepperState.activeDonorPortal.id}`
        : undefined

      const { data } = await client.mutate({
        mutation,
        variables: stepperState.isGuest
          ? { userId: stepperState.user?.id, data: baseVariables }
          : { data: baseVariables },
        ...(activePortalHeader && {
          context: { headers: { 'x-active-portal': activePortalHeader } },
        }),
      })

      const result = stepperState.isGuest ? data?.createGuestDonation : data?.createDonation
      if (!result?.success) throw new Error(result.error)
      const contributions = result.contributions || []

      // Success
      dispatchStep({
        action: 'NEXT',
        state: {
          success: {
            fund: stepperState.isGuest ? null : stepperState.activeDonorPortal ?? defaultFund,
            fundId: Number(fundId),
            donationTargetName: isDonatingToFund ? donationTarget.name : donationTarget.accountName,
            donationTargetId,
            paymentMethodType,
            isRecurring,
            processingDate,
            grantTotal: amounts.total,
            grantAmount: amounts.amount,
            securities: amountType === 'securities' ? securities : [],
            payment: stepperState.payment,
            contributions,
          },
        },
      })
    } catch (e) {
      setAlert({
        timeout: longWait,
        message: 'Something went unexpectedly wrong while making the donation',
        error: e.message,
        severity: 'error',
      })
    }
  }

  const amounts = calculateGiftAmount(stepperState.giftDetails)

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={isSubmitting} style={{ display: 'contents', border: 0, p: 0, m: 0 }}>
          <Stack direction="column" spacing={2}>
            <Typography component="h2" variant="h6">
              Review and Confirm
            </Typography>

            <ContentBox border>
              <Stack direction="column" spacing={2} divider={<Divider orientation="horizontal" flexItem />}>
                {stepperState.giftDetails.amountType === 'securities' &&
                  stepperState.payment?.selectedPath === 'advisor' && (
                    // Path A: Advisor - show intended amount only
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                      <Typography>Intended Contribution Amount</Typography>
                      <Typography>{toAmountString(fromAmountField(stepperState.giftDetails.amount || '0'))}</Typography>
                    </Stack>
                  )}
                {stepperState.giftDetails.amountType === 'securities' &&
                  stepperState.payment?.selectedPath !== 'advisor' && (
                    // Path B: Self-directed - show securities list
                    <Stack direction="column" spacing={1}>
                      {stepperState.giftDetails.securities.map((sec, index) => {
                        const quantity = fromAmountField(sec.quantity || '0')
                        const amount = fromAmountField(sec.amount || '0')

                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <Stack key={index} direction="row" spacing={2} justifyContent="space-between">
                            <Stack direction="column" spacing={0}>
                              <Typography>{`${sec.securitiesDescription}`}</Typography>
                              <Typography color="textSecondary" variant="body2">
                                ({`${sec.fundSymbolCUSIP}`})
                              </Typography>
                            </Stack>
                            <Stack direction="column" spacing={0} alignItems="flex-end">
                              <Typography>{quantity} Units</Typography>
                              <Typography color="textSecondary" variant="body2">
                                ~{toAmountString(amount)}
                              </Typography>
                            </Stack>
                          </Stack>
                        )
                      })}

                      <Divider orientation="horizontal" />

                      <Stack direction="row" spacing={2} justifyContent="space-between">
                        <Typography>Approx Total Value</Typography>
                        <Typography>{toAmountString(amounts.amount)}</Typography>
                      </Stack>
                    </Stack>
                  )}
                {stepperState.giftDetails.amountType !== 'securities' && (
                  <>
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                      <Typography>Donation Amount</Typography>
                      <Typography>{toAmountString(amounts.amount)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                      <Typography>Fees Covered</Typography>
                      <Typography>{toAmountString(amounts.tip)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                      <Typography>Total Amount</Typography>
                      <Typography>{toAmountString(amounts.total)}</Typography>
                    </Stack>
                  </>
                )}
                <Stack>
                  <Stack direction="column" spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Donation scheduled for:
                    </Typography>
                    <Typography>
                      {stepperState.giftDetails?.processingDate
                        ? toDateString(new Date(stepperState.giftDetails.processingDate))
                        : 'Now'}
                    </Typography>
                  </Stack>
                  <Stack direction="column" spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Donation Interval:
                    </Typography>
                    <Typography>
                      {stepperState.giftDetails?.isRecurring
                        ? { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Annually' }[
                            stepperState.giftDetails.recurringInterval
                          ] || stepperState.giftDetails.recurringInterval
                        : 'One-Time'}
                    </Typography>
                  </Stack>
                  <Stack direction="column" spacing={0}>
                    <Typography variant="caption" color="text.secondary">
                      Gift Purpose:
                    </Typography>
                    <Typography>{stepperState.giftDetails?.purposeOfGrant}</Typography>
                  </Stack>
                  {stepperState.giftDetails?.purposeOfGrantNote && (
                    <Stack direction="column" spacing={0}>
                      <Typography variant="caption" color="text.secondary">
                        Note:
                      </Typography>
                      <Typography>{stepperState.giftDetails?.purposeOfGrantNote}</Typography>
                    </Stack>
                  )}
                </Stack>
                <Stack spacing={2}>
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register('anonymous')}
                          checked={watch('anonymous')}
                          onChange={(e) => {
                            setValue('anonymous', e.target.checked, { shouldValidate: true })
                          }}
                        />
                      }
                      label="I wish to remain anonymous"
                    />
                  </FormControl>
                </Stack>
              </Stack>

              <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
            </ContentBox>

            <Stack direction="row" spacing={2}>
              <Button type="button" onClick={() => dispatchStep({ action: 'PREV' })} disabled={isSubmitting}>
                Back
              </Button>
              <Box flexGrow={1} />
              <Button type="submit" startIcon={<Icons.Lock style={{ fontSize: '1.25em' }} />} disabled={isSubmitting}>
                {stepperState.giftDetails.isRecurring ? 'Schedule Gift' : 'Give Now'}
              </Button>
            </Stack>
          </Stack>
        </fieldset>
      </form>
    </Container>
  )
}
